import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, confirmSignIn } from "aws-amplify/auth";

import { PASSWORD } from "@helpers/regex";
import { useLazyGetCurrentUserInfoV2Query } from "@services/api/users";
import { setSignInStep, verifiedOTP } from "@store/slices/sessionSlice";

export const FormularioCambioContrasenia = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [error, setError] = useState<boolean>(false);

    const hasFetched = useRef(false);

    const [triggerGetCurrentUserInfo] = useLazyGetCurrentUserInfoV2Query();

    const formik = useFormik({
        initialValues: {
            password: "",
            password_confirm: "",
        },
        validationSchema: yup.object().shape({
            password: yup
                .string()
                .matches(
                    PASSWORD,
                    "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
                )
                .required("Campo requerido"),
            password_confirm: yup
                .string()
                .matches(
                    PASSWORD,
                    "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
                )
                .required("Campo requerido"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await confirmSignIn({
                    challengeResponse: values.password,
                });

                const currentUser = await getCurrentUser();

                await triggerGetCurrentUserInfo(currentUser.userId).unwrap();
                dispatch(verifiedOTP());
                dispatch(setSignInStep("WELCOME"));
                navigate("/solicitudes");
            } catch (error) {
            } finally {
                setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        setError(formik.values.password !== formik.values.password_confirm);
    }, [formik.values]);

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Stack
                direction={"column"}
                sx={{
                    my: 2,
                    mx: 4,
                    justifyContent: "flex-start",
                    alignItems: "center",
                    rowGap: 2
                }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 700,
                        color: "#002652"
                    }}>
                    ¡Hola!
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        my: 3,
                        textAlign: "center",
                        color: "#002652"
                    }}>
                    Actualiza tu contraseña
                </Typography>
                <TextField
                    id="password"
                    label="Contraseña"
                    type="password"
                    required
                    size="small"
                    sx={{ maxWidth: "60%" }}
                    autoComplete="new-password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.password && Boolean(formik.errors.password)) ?? false}
                    helperText={formik.touched.password && formik.errors.password}
                    value={formik.values.password}
                />
                <TextField
                    id="password_confirm"
                    label="Confirma tu contraseña"
                    type="password"
                    required
                    size="small"
                    sx={{ maxWidth: "60%" }}
                    autoComplete="new-password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.password_confirm && Boolean(formik.errors.password_confirm)) ?? false}
                    helperText={formik.touched.password_confirm && formik.errors.password_confirm}
                />

                {error && (
                    <Box sx={{ mt: 2 }}>
                        <Alert variant="outlined" severity="error">
                            Las contraseñas no coinciden
                        </Alert>
                    </Box>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={error}
                    loading={formik.isSubmitting}
                    sx={{ py: 1, mb: 5, mt: 3, maxWidth: "60%" }}
                >
                    Actualizar contraseña
                </Button>
            </Stack>
        </form>
    );
};
